import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GoogleLogo, Sparkle, ShieldCheck, EnvelopeSimple, CalendarBlank, CheckCircle } from "@phosphor-icons/react";
import { api, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SCOPES = [
  { icon: EnvelopeSimple, title: "Gmail", desc: "Reads your recent inbox threads (read-only)" },
  { icon: CalendarBlank, title: "Google Calendar", desc: "Reads your upcoming events & detects conflicts" },
];

export default function Connect({ onConnected }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const connect = async () => {
    setLoading(true);
    try {
      await api.connectGoogle();
      await onConnected();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Sync failed. Please try again.";
      if (status === 428) {
        toast.error("Please sign in again to grant Gmail & Calendar access");
        clearToken();
        navigate("/login");
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center mb-5">
            <Sparkle weight="fill" className="text-white" size={28} />
          </div>
          <h1 className="font-heading text-4xl font-light tracking-tight text-slate-900">Meet Auppo</h1>
          <p className="text-slate-500 mt-3 max-w-sm leading-relaxed">
            Your AI executive assistant. Sync your Gmail &amp; Calendar so Auppo can triage your inbox,
            summarize threads, and draft replies — <span className="font-medium text-slate-700">nothing is sent without your approval.</span>
          </p>
        </div>

        <div className="bg-white border border-border rounded-2xl p-6 sm:p-8">
          <div className="space-y-4 mb-6">
            {SCOPES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3.5">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Icon size={20} className="text-slate-700" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{title}</div>
                  <div className="text-sm text-slate-500">{desc}</div>
                </div>
                <CheckCircle weight="fill" className="text-green-700 ml-auto mt-2" size={18} />
              </div>
            ))}
          </div>

          <Button
            data-testid="connect-google-btn"
            onClick={connect}
            disabled={loading}
            className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-medium gap-2.5"
          >
            <GoogleLogo weight="bold" size={20} />
            {loading ? "Syncing your Gmail & Calendar…" : "Sync my Gmail & Calendar"}
          </Button>

          <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-slate-400">
            <ShieldCheck size={14} />
            Read-only · your data stays private to your account
          </div>
        </div>
      </motion.div>
    </div>
  );
}
